import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { fetchMe, logout as apiLogout, startGoogleLogin } from "../lib/auth";
import { resetAnalytics } from "../lib/analytics";
import TeacherProfileModal from "../components/TeacherProfileModal";
import FirstRunFlow from "../components/onboarding/FirstRunFlow";

const AuthContext = createContext({
  user: null,
  loading: true,
  signIn: () => {},
  signOut: async () => {},
  refresh: async () => null,
  openProfileModal: () => {},
  replayFirstRun: () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileModal, setProfileModal] = useState({ open: false, firstTime: false });
  const initialized = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const me = await fetchMe();
      setUser(me);
      return me;
    } catch (_e) {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    // Skip /me when returning from the OAuth callback — AuthCallback will
    // exchange the session_id and refresh us itself.
    if (
      typeof window !== "undefined" &&
      window.location.hash &&
      window.location.hash.includes("session_id=")
    ) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  // First-time prompt — auto-open the modal once we know the user is logged
  // in but hasn't completed their classroom profile yet. Suppressed on the
  // /my-lesson/* magic-link landing so a brand-new teacher reading their
  // free lesson preview isn't immediately gated behind a profile form —
  // they get to see the value first, the modal kicks in if/when they head
  // into the dashboard. Also suppressed on /lesson-from-resource (previously
  // /try — kept for the SPA soft-redirect landing) — the upload-first free
  // funnel is supposed to be friction-free; teachers should be able to
  // claim their free lesson before being asked about classroom setup.
  useEffect(() => {
    if (!user) return;
    if (user.profile_complete) return;
    if (typeof window !== "undefined") {
      const path = window.location?.pathname || "";
      if (path.startsWith("/my-lesson")) return;
      if (path === "/try" || path.startsWith("/try/")) return;
      if (path === "/lesson-from-resource" || path.startsWith("/lesson-from-resource/")) return;
    }
    setProfileModal({ open: true, firstTime: true });
  }, [user]);

  const signOut = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      setUser(null);
      resetAnalytics();
      // Phase 126.G — always land on the homepage after sign-out so
      // teachers don't see a flashing dashboard / paywall stub before
      // the redirect kicks in. Hard reload (assign) clears every
      // cached query + React state cleanly.
      if (typeof window !== "undefined") {
        window.location.assign("/");
      }
    }
  }, []);

  const openProfileModal = useCallback(() => {
    setProfileModal({ open: true, firstTime: false });
  }, []);

  const handleProfileClose = useCallback(() => {
    // Only allow close when not in the first-time gating flow.
    setProfileModal((m) => (m.firstTime ? m : { ...m, open: false }));
  }, []);

  const handleProfileSaved = useCallback(
    (res) => {
      setUser((u) =>
        u
          ? {
              ...u,
              grades_taught: res?.grades_taught || u.grades_taught || [],
              subjects_taught: res?.subjects_taught || u.subjects_taught || [],
              privacy_consent_at: res?.privacy_consent_at ?? u.privacy_consent_at ?? null,
              profile_complete: !!res?.profile_complete,
            }
          : u
      );
      setProfileModal({ open: false, firstTime: false });
    },
    []
  );

  const [firstRunReplayNonce, setFirstRunReplayNonce] = useState(0);
  const replayFirstRun = useCallback(() => setFirstRunReplayNonce((n) => n + 1), []);

  const value = useMemo(
    () => ({
      user,
      loading,
      signIn: startGoogleLogin,
      signOut,
      refresh,
      setUser,
      openProfileModal,
      replayFirstRun,
    }),
    [user, loading, refresh, signOut, openProfileModal, replayFirstRun]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {user && (
        <TeacherProfileModal
          open={profileModal.open}
          isFirstTime={profileModal.firstTime}
          initialGrades={user.grades_taught || []}
          initialSubjects={user.subjects_taught || []}
          initialConsentAt={user.privacy_consent_at || null}
          onClose={handleProfileClose}
          onSaved={handleProfileSaved}
        />
      )}
      {user && (
        <FirstRunFlow
          profileGateActive={profileModal.open && profileModal.firstTime}
          replayNonce={firstRunReplayNonce}
        />
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
